import React, { useContext, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { PreferencesContext } from 'context/PreferencesProvider';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';

const AGLOCampaignSelector = () => {
  const {
    campaignYear,
    selectedCampaignYear,
    setSelectedCampaignYear,
    availableCampaigns,
    areCampaignsLoading,
    isHistoricalCampaign,
    isCampaignSwitching,
    writesInProgress,
  } = useContext(PreferencesContext);
  const [pendingYear, setPendingYear] = useState(null);
  const current = campaignYear ? { year: campaignYear } : null;
  const historical = availableCampaigns
    ?.filter((campaign) => Number(campaign.year) !== Number(campaignYear))
    .sort((a, b) => b.year - a.year);
  const disabled = !current || areCampaignsLoading || isCampaignSwitching || writesInProgress > 0;

  const selectCampaign = (year) => {
    if (disabled || Number(year) === Number(selectedCampaignYear)) return;
    if (!isHistoricalCampaign && Number(year) !== Number(campaignYear)) {
      setPendingYear(Number(year));
      return;
    }
    setSelectedCampaignYear(Number(year));
  };

  const confirmCampaign = () => {
    if (pendingYear == null || disabled) return;
    setSelectedCampaignYear(pendingYear);
    setPendingYear(null);
  };

  return (
    <>
      <div className="px-[7px] pb-3">
        <Select
          value={selectedCampaignYear ? String(selectedCampaignYear) : ''}
          onValueChange={selectCampaign}
          disabled={disabled}
        >
          <SelectTrigger
            aria-label={`Campagne consultée : ${selectedCampaignYear || 'indisponible'}, ${
              isHistoricalCampaign ? 'lecture seule' : 'en cours'
            }`}
            className="h-[58px] gap-2 rounded-lg border-[#d5e1d8] bg-[#f0f5f1] px-2.5 text-left text-[#244c34] shadow-none transition-colors hover:border-[#a9c2b1] hover:bg-[#e9f1eb] focus:ring-hajj-primary data-[state=open]:border-[#86ae93] data-[state=open]:bg-[#e7f0e9] [&>svg:last-child]:h-[13px] [&>svg:last-child]:w-[13px] [&>svg:last-child]:shrink-0"
          >
            <CalendarDays className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
            <SelectValue asChild>
              <span className="!flex min-w-0 flex-1 flex-col items-start gap-0.5 !overflow-visible leading-tight">
                <span className="whitespace-nowrap text-[13px] font-semibold leading-[18px]">
                  Campagne {selectedCampaignYear || '—'}
                </span>
                <span className="text-[11px] leading-[15px] text-[#61796a]">
                  {isHistoricalCampaign ? 'Lecture seule' : 'En cours'}
                </span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            align="start"
            sideOffset={1}
            className="max-h-[310px] w-[var(--radix-select-trigger-width)] border-[#d8e2db] p-1 shadow-[0_5px_15px_#1d3b2914] [&_[data-radix-select-viewport]]:!h-auto"
          >
            <SelectGroup>
              <SelectLabel className="px-[7px] py-1 text-[10px] font-medium leading-4 text-[#728278]">
                Campagne en cours
              </SelectLabel>
              {current && (
                <SelectItem
                  value={String(current.year)}
                  className="h-[34px] rounded px-2 pl-7 text-[13px] focus:bg-[#f4f7f5] data-[state=checked]:bg-[#eaf3ed] data-[state=checked]:font-semibold data-[state=checked]:text-[#235c3b] [&>span:last-child]:flex [&>span:last-child]:w-full"
                >
                  {current.year} <span className="ml-auto text-[10px] font-normal text-[#6a7c70]">En cours</span>
                </SelectItem>
              )}
            </SelectGroup>
            {historical?.length > 0 && (
              <SelectGroup className="mt-1 border-t border-[#edf1ee] pt-1">
                <SelectLabel className="px-[7px] py-1 text-[10px] font-medium leading-4 text-[#728278]">
                  Campagnes antérieures
                </SelectLabel>
                {historical.map((campaign) => (
                  <SelectItem
                    key={campaign.id || campaign.year}
                    value={String(campaign.year)}
                    className="h-[34px] rounded px-2 pl-7 text-[13px] focus:bg-[#f4f7f5] data-[state=checked]:bg-[#eaf3ed] data-[state=checked]:font-semibold data-[state=checked]:text-[#235c3b]"
                  >
                    {campaign.year}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>
      <ConfirmationModal
        isOpen={pendingYear != null}
        setOpenModal={(open) => !open && setPendingYear(null)}
        title={`Consulter la campagne ${pendingYear} ?`}
        message="Cette campagne est disponible en lecture seule. Les exports restent accessibles."
        confirmBtnLabel={`Consulter ${pendingYear}`}
        onConfirm={confirmCampaign}
      />
    </>
  );
};

export default AGLOCampaignSelector;
